import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionCardStyle,
  renderPublicHero,
} from "../../../components/general/publicPageStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import PublicWorkflowSteps from "../components/PublicWorkflowSteps";

const emarWorkflowSteps = [
  {
    title: "Import and scan",
    description: "Use a care plan or MAR sheet. The system reads the medication list.",
    icon: "pi pi-file-edit",
  },
  {
    title: "Review medications",
    description: "Confirm the generated medication list before charting.",
    icon: "pi pi-search",
  },
  {
    title: "Start charting",
    description: "Track medications, vitals, PRNs, notes, and exceptions.",
    icon: "pi pi-pencil",
  },
  {
    title: "Download report",
    description: "Create a DSHS-format report with charted meds and initials.",
    icon: "pi pi-file-pdf",
  },
] as const;

const emarFeatureRows = [
  {
    feature: "Track scheduled, PRN, and varied medication routines",
    whatItDoes: "Organize routine medications, as-needed medications, one-time instructions, topical medications, inhalers, insulin, and other resident-specific schedules in one active list.",
    whyItSavesTime: "Keeps different medication types and administration instructions together for easier review and charting.",
  },
  {
    feature: "Support vitals and sliding-scale instructions",
    whatItDoes: "Set up vital trackers and document sliding-scale or protocol-based instructions alongside the resident's medication workflow.",
    whyItSavesTime: "Puts medication-related observations and instructions where staff already complete daily charting.",
  },
  {
    feature: "Chart administrations with clear status and notes",
    whatItDoes: "Record administered, refused, held, missed, or otherwise excepted medication events with staff initials and notes when follow-up is needed.",
    whyItSavesTime: "Makes the daily record easier to scan while preserving the context behind exceptions.",
  },
  {
    feature: "Generate a DSHS-format monthly report",
    whatItDoes: "Create a monthly medication report with charted medications, staff initials, color-coded administration status, notes, and exceptions.",
    whyItSavesTime: "Turns daily charting into a clearer monthly record for review, follow-up, and compliance workflows.",
  },
] as const;

const EmarPage = () => {
  const authExperience = useAuthExperience();
  const mobile = useResponsive().isMobile;

  return (
    <div style={publicPageContainerStyle}>
      <PublicWorkflowSteps
        title="Start eMAR in a few clear steps"
        description="Use the records you already have and let the system do the repetitive setup work before your team reviews and starts charting."
        steps={emarWorkflowSteps}
      />
      <div className="mt-3" style={publicSectionCardStyle}>
        <div style={{ color: "#0f3d63", fontSize: mobile ? "1.4rem" : "1.75rem", fontWeight: 800, textAlign: "center" }}>
          eMAR features for everyday charting
        </div>
        <p style={{ ...publicMutedTextStyle, textAlign: "center", margin: "0.75rem auto 0", maxWidth: "820px" }}>
          Start with existing documentation, keep resident-specific decisions with the care team, and make every medication pass easier to review.
        </p>

        {mobile ? (
          <div className="mt-4 flex flex-column gap-3">
            {emarFeatureRows.map((row) => (
              <div
                key={row.feature}
                style={{
                  border: "1px solid #dde9f5",
                  borderRadius: "18px",
                  padding: "1rem",
                  background: "#ffffff",
                }}
              >
                <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1rem" }}>{row.feature}</div>
                <div className="mt-2" style={publicMutedTextStyle}>{row.whatItDoes}</div>
                <div className="mt-2" style={{ color: "#166534", fontWeight: 700, lineHeight: 1.7 }}>{row.whyItSavesTime}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "860px" }}>
              <thead>
                <tr style={{ background: "#f8fbff" }}>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Feature</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>What Hub Provides</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Why It Saves Time</th>
                </tr>
              </thead>
              <tbody>
                {emarFeatureRows.map((row) => (
                  <tr key={row.feature}>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#0f172a", fontWeight: 700, verticalAlign: "top" }}>{row.feature}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#475569", verticalAlign: "top", lineHeight: 1.7 }}>{row.whatItDoes}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#166534", fontWeight: 700, verticalAlign: "top", lineHeight: 1.7 }}>{row.whyItSavesTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3" style={{ ...publicSectionCardStyle, textAlign: "center" }}>
        <div style={{ color: "#0f3d63", fontSize: mobile ? "1.35rem" : "1.7rem", fontWeight: 800 }}>
          Clear records from daily charting to monthly review
        </div>
        <p style={{ ...publicMutedTextStyle, margin: "0.75rem auto 0", maxWidth: "780px" }}>
          {`${authExperience.brandName} keeps charted medications, initials, color-coded statuses, notes, and exceptions together so the monthly record is easier to review.`}
        </p>
      </div>
    </div>
  );
};

export default EmarPage;
